import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { generateSupplierBookingReportMutation } from "@/app/Suppliers/GraphQL/supplierBookingReportsQuery";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { toast } from "sonner";
import { SupplierInput } from "@/app/Suppliers/Components/SupplierInput";
import { useSupplier } from "@/app/Suppliers/Hooks/useSupplierRecord";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Suspense } from "react";
import { useFormikContext } from "formik";

type GenerateBookingReportDialogProps = {
  supplierId?: string;
};

type FormValues = {
  supplier_id: string;
  period: string;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getMonthOptions(createdAt: string) {
  const start = new Date(createdAt);
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const options: { value: string; label: string }[] = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);

  while (current <= lastMonth) {
    const month = current.getMonth() + 1;
    const year = current.getFullYear();
    options.push({
      value: `${year}-${month}`,
      label: `${MONTH_NAMES[month - 1]} ${year}`,
    });
    current.setMonth(current.getMonth() + 1);
  }

  return [{ label: "Period", options: options.reverse() }];
}

function getDefaultPeriod() {
  const now = new Date();
  const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return `${year}-${lastMonth}`;
}

function GenerateBookingReportPeriodSelect({
  supplierId,
}: {
  supplierId: string;
}) {
  const supplier = useSupplier(supplierId);
  const periodOptions = getMonthOptions(supplier.created_at);

  return <SelectInput name="period" label="Period" options={periodOptions} />;
}

function SupplierSelectedPeriodSelect() {
  const { values } = useFormikContext<FormValues>();

  if (!values.supplier_id) {
    return null;
  }

  return (
    <Suspense
      fallback={<div className="text-sm text-gray-400">Loading periods...</div>}
    >
      <GenerateBookingReportPeriodSelect supplierId={values.supplier_id} />
    </Suspense>
  );
}

export const GenerateBookingReportDialog: DialogComponent<
  GenerateBookingReportDialogProps
> = ({ supplierId: supplierIdProp }) => {
  const { close } = useDialogState();
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const resolvedSupplierId = supplierIdProp ?? globalSupplierId ?? undefined;
  const { mutateAsync } = useGqlMutation(generateSupplierBookingReportMutation);

  return (
    <BaseDialog title="Generate Booking Report">
      <GenericForm<FormValues>
        initialValues={{
          supplier_id: resolvedSupplierId ?? "",
          period: getDefaultPeriod(),
        }}
        onSubmit={async (values) => {
          const [year, month] = values.period.split("-").map(Number);
          await mutateAsync({
            supplierId: values.supplier_id,
            month,
            year,
          });

          toast.success(
            "Report is being generated and will be emailed when ready.",
          );

          await close();
        }}
      >
        {resolvedSupplierId ? null : (
          <SupplierInput name="supplier_id" label="Supplier" />
        )}
        {resolvedSupplierId ? (
          <Suspense
            fallback={
              <div className="text-sm text-gray-400">Loading periods...</div>
            }
          >
            <GenerateBookingReportPeriodSelect
              supplierId={resolvedSupplierId}
            />
          </Suspense>
        ) : (
          <SupplierSelectedPeriodSelect />
        )}
        <DialogSubmitButton text="Generate Report" />
      </GenericForm>
    </BaseDialog>
  );
};
